// Pure link-preview resolver for the "Add content URL" modal.
//
// Given a raw URL string, returns a fully-resolved, discriminated preview
// descriptor — the UI layer only reads these fields, it never re-parses. All
// three supported platforms resolve to a FREE, browser-side source:
//   • YouTube   → youtube.com/embed iframe
//   • Drive     → drive.google.com/file/d/<id>/preview iframe
//   • Instagram → instagram.com/p/<code>/embed iframe (official public embed)
//
// No API keys, no scrapers, no backend call. Keeping this as plain data-returning
// functions (no JSX, no `window`) makes every branch unit-testable in isolation.

import { parseYouTubeVideoId } from './youtubeThumbnail';

export type PreviewPlatform = 'youtube' | 'drive' | 'instagram';

/**
 * Resolved preview descriptor. The UI switches on `kind` and reads the already
 * computed URLs — it performs no parsing or conditionals of its own.
 *
 *  - `empty`         → input is blank; render nothing.
 *  - `unsupported`   → a URL we can't preview (non-platform host); render nothing.
 *  - `pending`       → a known platform on a previewable path (a video / file /
 *                      post) whose id isn't complete yet — user is mid-entry;
 *                      render a skeleton.
 *  - `unpreviewable` → a known platform host, but the URL points at something we
 *                      can't embed (a channel, a Drive folder, an IG profile).
 *                      A settled state — render a muted "can't preview" note, not
 *                      a skeleton that never resolves.
 *  - `youtube` / `drive` / `instagram` → a real preview.
 */
export type LinkPreview =
  | { kind: 'empty' }
  | { kind: 'unsupported' }
  | { kind: 'pending'; platform: PreviewPlatform }
  | { kind: 'unpreviewable'; platform: PreviewPlatform }
  | { kind: 'youtube'; videoId: string; embedUrl: string }
  | { kind: 'drive'; fileId: string; embedUrl: string }
  | { kind: 'instagram'; type: string; shortcode: string; embedUrl: string };

const YOUTUBE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'youtu.be',
]);
const DRIVE_HOSTS = new Set(['drive.google.com']);
// Google Docs is never accepted on any URL input (content or live) — content is
// a video file shared from Drive. Mirrors the BE's googleDocsHosts.
const GOOGLE_DOCS_HOSTS = new Set(['docs.google.com']);
const INSTAGRAM_HOSTS = new Set(['instagram.com', 'www.instagram.com']);

/** Which platform (and phase) a pasted URL must match — shared by
 *  UrlInputModal (Save-time validation) and LinkPreviewCard (preview
 *  gating). */
export type RequirePlatform =
  | 'youtube'
  | 'instagram'
  | 'youtube-live'
  | 'instagram-live';

/** Every previewable platform — the `undefined` (any well-formed HTTPS URL)
 *  mode allows all of them. */
export const ALL_PREVIEW_PLATFORMS: ReadonlySet<PreviewPlatform> = new Set([
  'youtube',
  'drive',
  'instagram',
]);

/** Which preview platforms each `RequirePlatform` mode accepts — the SINGLE
 *  source of truth for both Save-time validation (UrlInputModal.validateUrl)
 *  and preview gating (LinkPreviewCard): a URL the preview renders can never
 *  be one Save then rejects, and a future host addition can't desync the
 *  two. */
export const ALLOWED_PLATFORMS_BY_MODE: Record<RequirePlatform, ReadonlySet<PreviewPlatform>> = {
  youtube: new Set(['youtube', 'drive']), // content review — YouTube or Drive
  // content review — Drive only: the AI service can't ingest an Instagram URL.
  instagram: new Set(['drive']),
  'youtube-live': new Set(['youtube']), // live monitor URL — YouTube only, no Drive
  'instagram-live': new Set(['instagram']), // live monitor URL — Instagram only, no Drive
};

/** Strip stray wrapping quotes / whitespace that often survive a copy from an
 *  email or chat client ("https://…" → https://…). Mirrors what the BE's URL
 *  validators expect after their own trim, so the preview resolves the exact
 *  string Save will submit — never a raw value the two disagree on. */
export function sanitizeUrlInput(raw: string): string {
  return raw.trim().replace(/^["'`]+|["'`]+$/g, '').trim();
}

/** Lower-cased hostname, or null when the string isn't a parseable URL. */
function hostnameOf(raw: string): string | null {
  try {
    return new URL(raw.trim()).hostname.toLowerCase();
  } catch {
    return null;
  }
}

/** True for a Google Docs link — rejected on every URL input, in every mode. */
export function isGoogleDocsUrl(url: string): boolean {
  const host = hostnameOf(url);
  if (!host) return false;
  return GOOGLE_DOCS_HOSTS.has(host);
}

/**
 * Maps a URL to one of the three supported platforms by hostname, or null for
 * anything else. Used both to guard the Drive id parse and to decide the
 * `pending` skeleton state while a full URL is still being typed.
 */
export function detectPlatform(url: string): PreviewPlatform | null {
  const host = hostnameOf(url);
  if (!host) return null;
  if (YOUTUBE_HOSTS.has(host)) return 'youtube';
  if (DRIVE_HOSTS.has(host)) return 'drive';
  if (INSTAGRAM_HOSTS.has(host)) return 'instagram';
  return null;
}

// Drive file id appears either as `/d/<id>` (file/doc links) or `?id=<id>`
// (open?id=… links). Ids are 10+ url-safe chars.
const DRIVE_FILE_ID_RE = /(?:\/d\/|[?&]id=)([\w-]{10,})/;

/** Extracts a Google Drive file id, or null. Host-agnostic by design —
 *  callers guard with detectPlatform to avoid cross-host false positives. */
export function parseDriveFileId(url: string): string | null {
  const m = url.match(DRIVE_FILE_ID_RE);
  return m?.[1] ?? null;
}

// "Previewable resource" markers — does the path reference the KIND of thing a
// platform can embed (a video / a file / a post), regardless of whether the id
// is complete yet? Used to tell a half-typed previewable URL (→ `pending`,
// keep the skeleton) apart from a structurally un-embeddable one — a channel,
// a Drive folder, an IG profile (→ `unpreviewable`, a settled note).
const YT_VIDEO_FORM = /[?&]v=|\/(?:shorts|embed|live|v)\/|\/watch\b/i;
const DRIVE_FILE_FORM = /\/d\/|[?&]id=/;
const IG_POST_FORM = /\/(?:p|reel|reels|tv)\//i;

// Real Instagram shortcodes are 11 chars (legacy ones 10). Requiring a minimum
// stops a paused mid-type from rendering a cascade of 404 embed frames
// (/p/C/embed, /p/Ca/embed, …) before the full code is entered.
const MIN_IG_SHORTCODE = 10;

// Instagram post/reel/tv links, optionally namespaced under a username
// (instagram.com/<user>/reel/<code>). `reels` (plural) normalizes to `reel`.
const INSTAGRAM_RE = /instagram\.com\/(?:[\w.]+\/)?(p|reel|reels|tv)\/([\w-]+)/;

/** Extracts the Instagram post type + shortcode, or null. */
export function parseInstagram(url: string): { type: string; shortcode: string } | null {
  const m = url.match(INSTAGRAM_RE);
  const rawType = m?.[1];
  const shortcode = m?.[2];
  if (!rawType || !shortcode) return null;
  const type = rawType === 'reels' ? 'reel' : rawType;
  return { type, shortcode };
}

/** Mirrors the BE's ContentKind — see discovr-backend's
 *  internal/roster/domain/content_kind.go. */
export type ContentKind = 'short' | 'video' | 'reel' | 'post';

/** Mirrors the BE's IsYouTubeShortsURL exactly (internal/common/youtube.go) —
 *  the path must START WITH /shorts/, checked on the parsed pathname (not a
 *  substring test on the raw URL, which could false-positive on a query
 *  param). A stricter test than parseInstagram/detectPlatform's own host
 *  matching needs, because a "short" classification here gates Save — it
 *  must never be more permissive than what AdminSetMonitorURL will accept. */
function isYouTubeShortsURL(url: string): boolean {
  try {
    return new URL(url).pathname.startsWith('/shorts/');
  } catch {
    return false;
  }
}

/** Mirrors the BE's IsInstagramReelURL (internal/common/instagram.go) — reel/
 *  reels is the first path segment, or follows a username
 *  (instagram.com/<user>/reel/<code>, the form a creator's profile links to).
 *  The BE canonicalises both shapes to instagram.com/reel/<code>/. */
function isInstagramReelURL(url: string): boolean {
  let segments: string[];
  try {
    segments = new URL(url).pathname.toLowerCase().replace(/^\//, '').split('/');
  } catch {
    return false;
  }
  const isReel = (s?: string) => s === 'reel' || s === 'reels';
  const isKind = (s?: string) => isReel(s) || s === 'p';
  if (isKind(segments[0])) return isReel(segments[0]);
  return segments.length >= 3 && /^[\w.]+$/.test(segments[0] ?? '') && isReel(segments[1]);
}

/** Best-effort content kind implied by a URL's own shape — used by the "Add
 *  live URL" modal to warn/block when a pasted URL doesn't match the row's
 *  committed deliverable (e.g. a regular YouTube video for a row that
 *  committed to Shorts). Returns null when the platform/shape can't be
 *  determined (mid-typing, or a URL form this parser doesn't recognise) —
 *  callers should not block on null, only on a confirmed mismatch. */
export function detectContentKind(url: string): ContentKind | null {
  switch (detectPlatform(url)) {
    case 'youtube':
      return isYouTubeShortsURL(url) ? 'short' : 'video';
    case 'instagram': {
      const ig = parseInstagram(url);
      if (!ig) return null;
      return isInstagramReelURL(url) ? 'reel' : 'post';
    }
    default:
      return null;
  }
}

/**
 * Resolves a raw URL into a preview descriptor. The host is classified FIRST via
 * detectPlatform, so a look-alike or nested host — `evil.com/watch?v=…`,
 * `youtube.com.evil.com/…`, `youtube.com@evil.com/…`, `evil.com/instagram.com/p/x/`
 * — is `unsupported`, never a platform embed. Only once the real host matches do
 * we parse the id. A previewable path with an incomplete id yields `pending`; a
 * non-embeddable path on a real host (channel / folder / profile) yields
 * `unpreviewable`; an unrecognised host yields `unsupported`.
 */
export function resolveLinkPreview(rawUrl: string): LinkPreview {
  const url = rawUrl.trim();
  if (!url) return { kind: 'empty' };

  switch (detectPlatform(url)) {
    case 'youtube': {
      const videoId = parseYouTubeVideoId(url);
      if (videoId) {
        return {
          kind: 'youtube',
          videoId,
          embedUrl: `https://www.youtube.com/embed/${videoId}`,
        };
      }
      return YT_VIDEO_FORM.test(url)
        ? { kind: 'pending', platform: 'youtube' }
        : { kind: 'unpreviewable', platform: 'youtube' };
    }

    case 'drive': {
      const fileId = parseDriveFileId(url);
      if (fileId) {
        return {
          kind: 'drive',
          fileId,
          embedUrl: `https://drive.google.com/file/d/${fileId}/preview`,
        };
      }
      return DRIVE_FILE_FORM.test(url)
        ? { kind: 'pending', platform: 'drive' }
        : { kind: 'unpreviewable', platform: 'drive' };
    }

    case 'instagram': {
      const ig = parseInstagram(url);
      if (ig && ig.shortcode.length >= MIN_IG_SHORTCODE) {
        return {
          kind: 'instagram',
          type: ig.type,
          shortcode: ig.shortcode,
          // Always the /p/ embed form — it serves every post type (post, reel, tv),
          // whereas /tv/<code>/embed can return a non-embeddable page.
          embedUrl: `https://www.instagram.com/p/${ig.shortcode}/embed`,
        };
      }
      return IG_POST_FORM.test(url)
        ? { kind: 'pending', platform: 'instagram' }
        : { kind: 'unpreviewable', platform: 'instagram' };
    }

    default:
      return { kind: 'unsupported' };
  }
}
